/**
 * Chart image extraction utilities for exporting ECharts as images
 */

import { LIGHT_THEME } from '../theme/constants';
import { createEChartsTheme } from '../theme/utils';

export interface ChartRef {
  id: string;
  echartInstance: any; // ECharts instance
}

/**
 * Registry to track chart instances for image extraction
 */
class ChartImageExtractor {
  private chartRefs = new Map<string, any>();

  /**
   * Register a chart instance for potential image extraction
   */
  registerChart(id: string, echartInstance: any) {
    this.chartRefs.set(id, echartInstance);
  }

  /**
   * Unregister a chart instance (cleanup)
   */
  unregisterChart(id: string) {
    this.chartRefs.delete(id);
  }

  /**
   * Extract image data URL from a specific chart
   */
  async extractChartImage(id: string): Promise<string | null> {
    const echartInstance = this.chartRefs.get(id);
    if (!echartInstance) {
      console.warn(`Chart instance not found for ID: ${id}`);
      return null;
    }

    try {
      // Store current chart option to restore later
      const currentOption = echartInstance.getOption();

      // Create light mode theme for image extraction
      const lightTheme = createEChartsTheme(LIGHT_THEME);

      // Create a light mode version of the chart option
      const lightModeOption = this.createLightModeOption(currentOption, lightTheme);

      // Temporarily apply light mode styling
      echartInstance.setOption(lightModeOption, true);

      // Get the data URL from ECharts instance with light mode styling
      const dataUrl = echartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2, // Higher resolution for better quality
        backgroundColor: '#ffffff'
      });

      // Restore original chart option
      echartInstance.setOption(currentOption, true);

      return dataUrl;
    } catch (error) {
      console.error(`Failed to extract image for chart ${id}:`, error);
      return null;
    }
  }

  /**
   * Extract images from multiple charts
   */
  async extractMultipleChartImages(chartIds: string[]): Promise<Map<string, string>> {
    const imageMap = new Map<string, string>();

    for (const id of chartIds) {
      const dataUrl = await this.extractChartImage(id);
      if (dataUrl) {
        imageMap.set(id, dataUrl);
      }
    }

    return imageMap;
  }

  /**
   * Get all registered chart IDs
   */
  getRegisteredChartIds(): string[] {
    return Array.from(this.chartRefs.keys());
  }

  /**
   * Clear all chart references (useful for cleanup)
   */
  clearAll() {
    this.chartRefs.clear();
  }

  /**
   * Create a light mode version of the chart option
   */
  private createLightModeOption(currentOption: any, lightTheme: any): any {
    // Deep clone the current option to avoid modifying the original
    const lightOption = JSON.parse(JSON.stringify(currentOption));

    // Override theme-dependent properties with light mode values
    if (lightOption.title) {
      if (Array.isArray(lightOption.title)) {
        lightOption.title.forEach((title: any) => {
          if (title.textStyle) {
            title.textStyle.color = lightTheme.textColor;
          }
        });
      } else if (lightOption.title.textStyle) {
        lightOption.title.textStyle.color = lightTheme.textColor;
      }
    }

    // Override tooltip styling
    if (lightOption.tooltip) {
      lightOption.tooltip.backgroundColor = lightTheme.tooltipBg;
      lightOption.tooltip.borderColor = lightTheme.borderColor;
      if (lightOption.tooltip.textStyle) {
        lightOption.tooltip.textStyle.color = lightTheme.textColor;
      }
    }

    // Override legend styling
    if (lightOption.legend) {
      if (Array.isArray(lightOption.legend)) {
        lightOption.legend.forEach((legend: any) => {
          if (legend.textStyle) {
            legend.textStyle.color = lightTheme.labelColor;
          }
        });
      } else if (lightOption.legend.textStyle) {
        lightOption.legend.textStyle.color = lightTheme.labelColor;
      }
    }

    // Override axis styling
    const overrideAxisStyles = (axis: any) => {
      if (axis) {
        if (Array.isArray(axis)) {
          axis.forEach((axisItem: any) => {
            if (axisItem.axisLine?.lineStyle) {
              axisItem.axisLine.lineStyle.color = lightTheme.borderColor;
            }
            if (axisItem.axisLabel) {
              axisItem.axisLabel.color = lightTheme.textColor;
            }
            if (axisItem.splitLine?.lineStyle) {
              axisItem.splitLine.lineStyle.color = lightTheme.splitLineColor;
            }
          });
        } else {
          if (axis.axisLine?.lineStyle) {
            axis.axisLine.lineStyle.color = lightTheme.borderColor;
          }
          if (axis.axisLabel) {
            axis.axisLabel.color = lightTheme.textColor;
          }
          if (axis.splitLine?.lineStyle) {
            axis.splitLine.lineStyle.color = lightTheme.splitLineColor;
          }
        }
      }
    };

    overrideAxisStyles(lightOption.xAxis);
    overrideAxisStyles(lightOption.yAxis);

    // Override text styling globally
    if (lightOption.textStyle) {
      lightOption.textStyle.color = lightTheme.textColor;
    }

    // Use light mode chart colors for series
    if (lightOption.color && Array.isArray(lightOption.color)) {
      // Keep existing colors as they are already theme-agnostic
      // The default chart colors work well for both light and dark backgrounds
    }

    return lightOption;
  }
}

// Export singleton instance
export const chartImageExtractor = new ChartImageExtractor();
